import {
  type AccessLevel,
  type AccessMatrixChange,
  type AccessMatrixResponse,
  getAccessMatrix,
  levelFromFlags,
  updateAccessMatrix,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@lms/ui/card';
import { Checkbox } from '@lms/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@lms/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@lms/ui/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '@lms/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lms/ui/select';
import { Separator } from '@lms/ui/separator';
import { useToast } from '@lms/ui/use-toast';
import { cn } from '@lms/ui/utils';
import { Ellipsis, LoaderCircle, Lock, Plus, TrashIcon } from 'lucide-react';
import * as React from 'react';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import { SearchFilter } from '@/components/search-filter';
import { useCurrentUser } from '@/features/auth/use-auth';
import { GroupSelector } from '@/features/student/components/group-selector';
import { removeUser } from '@/features/users/users-api';
import { AddUserDialog } from './add-user-dialog';
import {
  flagsOf,
  LEVEL_META,
  LEVELS,
  type LevelFlags,
  levelOf,
} from './levels';

const key = (userId: number, locationId: number) => `${userId}:${locationId}`;

// Crosshair hover: every cell carries two transparent overlays. ::before lights
// up with its row (plain CSS), ::after with its user column (one rule written
// by the grid for the hovered column), so the cell under the pointer gets both
// and reads darker. Keyboard focus drives the same crosshair. The overlays
// need a positioned cell: `relative` on plain cells, `sticky` on headers.
const ROW_OVERLAY =
  "before:pointer-events-none before:absolute before:inset-0 before:bg-primary/10 before:opacity-0 before:content-[''] [tr:hover>&]:before:opacity-100 [tr:focus-within>&]:before:opacity-100";
const COL_OVERLAY =
  "am-col after:pointer-events-none after:absolute after:inset-0 after:bg-primary/10 after:opacity-0 after:content-['']";

export const accessMatrixKey = (portfolioId: number) =>
  ['access-matrix', portfolioId] as const;

// ---------------------------------------------------------------------------
// A single memoised cell: a button sharing one stable click handler, which
// reads the cell from data-* attributes (Enter/Space arrive as clicks), so a
// cell only re-renders when its own level changes.
// Locked cells (users who outrank the caller) are disabled.
// ---------------------------------------------------------------------------
const Cell = React.memo(function Cell({
  userId,
  locationId,
  level,
  label,
  ariaLabel,
  locked,
  onClick,
}: {
  userId: number;
  locationId: number;
  level: AccessLevel;
  label: string;
  ariaLabel: string;
  locked: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const meta = LEVEL_META[level];
  const Icon = meta.Icon;
  return (
    <td
      data-col={userId}
      className={cn(
        'h-12 border-b border-l border-border/40 p-0 align-middle text-xs whitespace-nowrap [tr:last-child>&]:border-b-0',
        'relative',
        ROW_OVERLAY,
        COL_OVERLAY,
        meta.cell,
      )}
    >
      <button
        type="button"
        data-u={userId}
        data-l={locationId}
        disabled={locked}
        aria-label={ariaLabel}
        onClick={onClick}
        className={cn(
          'flex h-full w-full items-center gap-1.5 px-2 text-left select-none',
          'focus-visible:ring-ring outline-none focus-visible:ring-2 focus-visible:ring-inset',
          locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        )}
      >
        {level === 'none' ? (
          <span className="text-muted-foreground/60">—</span>
        ) : (
          <>
            <Icon
              className={cn('h-3.5 w-3.5 shrink-0', meta.icon)}
              aria-hidden
            />
            {label}
          </>
        )}
      </button>
    </td>
  );
});

// Card on the users page: the user × location access grid for a portfolio.
export function AccessMatrixSection({
  portfolioId,
  customerName,
}: {
  portfolioId: number;
  customerName: string;
}) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { data, isLoading, mutate } = useSWR(accessMatrixKey(portfolioId), () =>
    getAccessMatrix(portfolioId),
  );

  // min-w-0: PageBox is a grid, and without it the card grows to the table's
  // full width instead of letting the table scroll inside it.
  return (
    <Card className="min-w-0">
      {isLoading || !data ? (
        <CardContent className="flex items-center justify-center py-12">
          <LoaderCircle className="text-primary size-8 animate-spin" />
        </CardContent>
      ) : (
        <MatrixGrid
          data={data}
          portfolioId={portfolioId}
          customerName={customerName}
          currentUserId={user?.id}
          isSuperAdmin={!!user?.superAdmin}
          onSaved={(fresh) => mutate(fresh, { revalidate: false })}
          refresh={() => mutate()}
          toast={toast}
        />
      )}
    </Card>
  );
}

function MatrixGrid({
  data,
  portfolioId,
  customerName,
  currentUserId,
  isSuperAdmin,
  onSaved,
  refresh,
  toast,
}: {
  data: AccessMatrixResponse;
  portfolioId: number;
  customerName: string;
  currentUserId: number | undefined;
  isSuperAdmin: boolean;
  onSaved: (fresh: AccessMatrixResponse) => void;
  refresh: () => Promise<unknown>;
  toast: ReturnType<typeof useToast>['toast'];
}) {
  const t = useTranslations('access_matrix');
  const tAdmin = useTranslations('admin');
  const levelLabel = (level: AccessLevel) => t(`level_${level}`);

  // Server baseline: level per (user, location); missing = 'none'.
  // Permissions only exist per location: the customer itself is just
  // membership, which every listed user has.
  const baseLevels = React.useMemo(() => {
    const m = new Map<string, AccessLevel>();
    for (const c of data.cells) {
      m.set(
        key(c.userId, c.locationId),
        levelFromFlags(c.canManageGroups, c.canManageUsers),
      );
    }
    return m;
  }, [data.cells]);

  // Users who outrank the caller; the backend rejects any change to them.
  const lockedUsers = React.useMemo(
    () => new Set(data.users.filter((u) => u.locked).map((u) => u.id)),
    [data.users],
  );

  // Changes apply immediately. While a request is in flight its cells show
  // the new level from here, so the grid doesn't wait on the round trip.
  const [inFlight, setInFlight] = React.useState<Map<string, AccessLevel>>(
    () => new Map(),
  );
  // Requests run one after another, so responses land in the order the
  // changes were made.
  const queue = React.useRef<Promise<unknown>>(Promise.resolve());
  // Selected ids; empty means all.
  const [userIds, setUserIds] = React.useState<number[]>([]);
  const [locIds, setLocIds] = React.useState<number[]>([]);
  const [region, setRegion] = React.useState<string>('all');

  const regions = React.useMemo(() => {
    const s = new Set<string>();
    for (const l of data.locations) if (l.region) s.add(l.region);
    return [...s].sort();
  }, [data.locations]);

  const users = React.useMemo(
    () =>
      userIds.length === 0
        ? data.users
        : data.users.filter((u) => userIds.includes(u.id)),
    [data.users, userIds],
  );

  const locations = React.useMemo(
    () =>
      data.locations.filter(
        (l) =>
          (region === 'all' || l.region === region) &&
          (locIds.length === 0 || locIds.includes(l.id)),
      ),
    [data.locations, locIds, region],
  );

  const effective = React.useCallback(
    (userId: number, locationId: number): AccessLevel => {
      const k = key(userId, locationId);
      return inFlight.get(k) ?? baseLevels.get(k) ?? 'none';
    },
    [inFlight, baseLevels],
  );

  // Save changes right away. On failure the grid falls back to what the
  // server has.
  const commit = (requested: AccessMatrixChange[]) => {
    const changes: AccessMatrixChange[] = [];
    let blockedSelf = false;
    for (const c of requested) {
      if (lockedUsers.has(c.userId)) continue;
      const current = effective(c.userId, c.locationId);
      if (current === c.level) continue;
      // A non-super-admin can't strip their own manage-users; the backend
      // rejects it too, but this avoids the failed round trip.
      if (
        !isSuperAdmin &&
        c.userId === currentUserId &&
        flagsOf(current).users &&
        !flagsOf(c.level).users
      ) {
        blockedSelf = true;
        continue;
      }
      changes.push(c);
    }
    if (blockedSelf) {
      toast({ variant: 'destructive', title: t('self_lockout') });
    }
    if (changes.length === 0) return;

    setInFlight((prev) => {
      const next = new Map(prev);
      for (const c of changes) next.set(key(c.userId, c.locationId), c.level);
      return next;
    });
    queue.current = queue.current.then(async () => {
      try {
        onSaved(await updateAccessMatrix(portfolioId, changes));
      } catch (err) {
        toast({
          variant: 'destructive',
          title: t('save_failed'),
          description: err instanceof Error ? err.message : String(err),
        });
        await refresh();
      } finally {
        // Leave a cell alone if a later change to it is still on its way.
        setInFlight((prev) => {
          const next = new Map(prev);
          for (const c of changes) {
            const k = key(c.userId, c.locationId);
            if (next.get(k) === c.level) next.delete(k);
          }
          return next;
        });
      }
    });
  };
  const applyLevels = (targets: Array<[number, number]>, level: AccessLevel) =>
    commit(
      targets.map(([userId, locationId]) => ({ userId, locationId, level })),
    );

  // One popover for the whole grid, anchored to whichever cell was opened.
  const [openCell, setOpenCell] = React.useState<{
    userId: number;
    locationId: number;
  } | null>(null);
  const anchorRef = React.useRef<HTMLElement | null>(null);

  // The user column under the pointer (or focus). Only a <style> rule changes,
  // so moving across the grid never re-renders the cells.
  const [hoverCol, setHoverCol] = React.useState<string | null>(null);
  const onCrosshair = (e: React.SyntheticEvent) => {
    const td =
      e.target instanceof Element
        ? (e.target.closest('td[data-col]') as HTMLElement | null)
        : null;
    setHoverCol(td?.dataset.col ?? null);
  };
  const clearCrosshair = () => setHoverCol(null);

  // Stable, so memoised cells don't re-render when the grid does.
  const onCellClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const cell = e.currentTarget;
      anchorRef.current = cell;
      setOpenCell({
        userId: Number(cell.dataset.u),
        locationId: Number(cell.dataset.l),
      });
    },
    [],
  );

  const setUserLevel = (userId: number, level: AccessLevel) =>
    applyLevels(
      locations.map((l) => [userId, l.id] as [number, number]),
      level,
    );
  const setLocationLevel = (locationId: number, level: AccessLevel) =>
    applyLevels(
      users.map((u) => [u.id, locationId] as [number, number]),
      level,
    );

  // Copy a whole row: every user's access at `source` onto `target`.
  // Covers all users, not just the filtered ones, so the result doesn't
  // depend on what happens to be visible.
  const [copyTarget, setCopyTarget] = React.useState<number | null>(null);
  const [copySource, setCopySource] = React.useState('');
  const copyChanges = React.useMemo(() => {
    if (copyTarget === null || !copySource) return [];
    const source = Number(copySource);
    return data.users
      .filter(
        (u) =>
          !lockedUsers.has(u.id) &&
          effective(u.id, source) !== effective(u.id, copyTarget),
      )
      .map((u) => u.id);
  }, [copyTarget, copySource, data.users, effective, lockedUsers]);
  const copyLocation = () => {
    if (copyTarget === null || !copySource) return;
    const source = Number(copySource);
    commit(
      copyChanges.map((userId) => ({
        userId,
        locationId: copyTarget,
        level: effective(userId, source),
      })),
    );
    setCopyTarget(null);
    setCopySource('');
  };
  const locationName = (id: number | null) =>
    data.locations.find((l) => l.id === id)?.name ?? '';
  const userName = (id: number | null) =>
    data.users.find((u) => u.id === id)?.name ?? '';

  const [addOpen, setAddOpen] = React.useState(false);
  const addUserLabel = tAdmin('add', { what: tAdmin('user').toLowerCase() });
  const [removeTarget, setRemoveTarget] = React.useState<number | null>(null);
  const [removing, setRemoving] = React.useState(false);

  const confirmRemove = async () => {
    if (removeTarget === null) return;
    setRemoving(true);
    try {
      await removeUser(removeTarget, portfolioId);
      await refresh();
      setRemoveTarget(null);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: t('delete_account'),
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRemoving(false);
    }
  };

  const levelMenu = (onPick: (level: AccessLevel) => void) =>
    LEVELS.map((lvl) => {
      const meta = LEVEL_META[lvl];
      const Icon = meta.Icon;
      return (
        <DropdownMenuItem key={lvl} onSelect={() => onPick(lvl)}>
          <Icon className={cn('h-3.5 w-3.5', meta.icon)} />
          {levelLabel(lvl)}
        </DropdownMenuItem>
      );
    });

  return (
    <>
      <CardHeader>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-52">
            <SearchFilter
              prefix={tAdmin('locations')}
              sections={[
                {
                  items: data.locations.map((l) => ({
                    id: l.id,
                    label: l.name,
                    keywords: [l.slug],
                  })),
                },
              ]}
              selected={locIds}
              onChange={setLocIds}
              searchPlaceholder={tAdmin('search')}
              emptyText={tAdmin('no_matches')}
              className="h-9"
            />
          </div>
          <div className="w-52">
            <SearchFilter
              prefix={tAdmin('users')}
              sections={[
                {
                  items: data.users.map((u) => ({
                    id: u.id,
                    label: u.name || u.email,
                    keywords: [u.email],
                  })),
                },
              ]}
              selected={userIds}
              onChange={setUserIds}
              searchPlaceholder={tAdmin('search')}
              emptyText={tAdmin('no_matches')}
              className="h-9"
            />
          </div>
          {regions.length > 0 && (
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="h-9 w-44">
                <SelectValue placeholder={t('region')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_regions')}</SelectItem>
                {regions.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <span className="text-muted-foreground ml-auto text-sm">
            {t('counts', {
              locations: locations.length,
              users: users.length,
            })}
          </span>
        </div>

        {locations.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">
            {t('nothing_to_show')}
          </p>
        ) : (
          <div className="max-h-[70vh] overflow-auto">
            {hoverCol !== null && (
              <style>{`.am-col[data-col="${hoverCol}"]::after{opacity:1}`}</style>
            )}
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="bg-background sticky top-0 left-0 z-30 min-w-56 border-b border-border px-2 py-3" />
                  {users.map((u) => (
                    <th
                      key={u.id}
                      data-col={u.id}
                      className={cn(
                        'bg-background sticky top-0 z-20 w-40 min-w-40 max-w-40 border-b border-l border-border/40 px-2 py-3 text-left align-bottom font-normal',
                        COL_OVERLAY,
                      )}
                    >
                      <div className="flex items-start gap-1">
                        <div
                          className="min-w-0 flex-1"
                          title={`${u.name}\n${u.email}`}
                        >
                          <div className="flex items-center gap-1 font-medium">
                            {u.locked && (
                              <Lock
                                className="text-muted-foreground h-3 w-3 shrink-0"
                                aria-label={t('locked')}
                              />
                            )}
                            <span className="truncate">{u.name}</span>
                          </div>
                          <div className="text-muted-foreground truncate text-xs">
                            {u.email}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              aria-label={t('actions_for', { name: u.name })}
                            >
                              <Ellipsis className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="max-w-72">
                            <DropdownMenuLabel className="truncate">
                              {u.name}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {u.locked ? (
                              <p className="text-muted-foreground px-2 py-1.5 text-xs">
                                {t('locked_help')}
                              </p>
                            ) : (
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  {t('set_for_user_locations', {
                                    count: locations.length,
                                  })}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {levelMenu((lvl) => setUserLevel(u.id, lvl))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            )}
                            {!u.locked && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  disabled={u.id === currentUserId}
                                  onSelect={() => setRemoveTarget(u.id)}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                  {t('delete_account')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </th>
                  ))}
                  {/* The add-user column and a borderless filler after it read
                      as one open last column: no header line, no right edge. */}
                  <th className="bg-background sticky top-0 z-20 w-12 min-w-12 border-l border-border/40" />
                  <th className="bg-background sticky top-0 z-20 w-full" />
                </tr>
              </thead>
              <tbody
                onMouseOver={onCrosshair}
                onFocus={onCrosshair}
                onMouseLeave={clearCrosshair}
                onBlur={clearCrosshair}
              >
                {/* Customer heading: locations are grouped under it. It has no
                    cells — permissions only exist per location. */}
                <tr>
                  <th className="bg-background sticky left-0 z-10 min-w-56 border-b border-border px-2 py-3 text-left font-semibold">
                    {customerName}
                  </th>
                  {users.map((u) => (
                    <td
                      key={u.id}
                      data-col={u.id}
                      className={cn(
                        'relative border-b border-l border-border/40',
                        COL_OVERLAY,
                      )}
                    />
                  ))}
                  {/* One clickable cell down the whole body: add a user. */}
                  <td
                    rowSpan={locations.length + 1}
                    className="relative border-l border-border/40 p-0"
                  >
                    <button
                      type="button"
                      onClick={() => setAddOpen(true)}
                      title={addUserLabel}
                      aria-label={addUserLabel}
                      className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring absolute inset-0 flex cursor-pointer items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-inset"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </td>
                  <td rowSpan={locations.length + 1} />
                </tr>
                {locations.map((l) => (
                  <tr key={l.id}>
                    <th
                      className={cn(
                        'bg-background sticky left-0 z-10 min-w-56 border-b border-border/40 py-3 pr-2 pl-6 text-left font-normal [tr:last-child>&]:border-b-0',
                        ROW_OVERLAY,
                      )}
                    >
                      <div className="flex items-center gap-1">
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{l.name}</div>
                          {l.region && (
                            <div className="text-muted-foreground truncate text-xs">
                              {l.region}
                            </div>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              aria-label={t('actions_for', { name: l.name })}
                            >
                              <Ellipsis className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="max-w-72"
                          >
                            <DropdownMenuLabel className="truncate">
                              {l.name}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                {t('set_for_location_users', {
                                  count: users.length,
                                })}
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent>
                                {levelMenu((lvl) =>
                                  setLocationLevel(l.id, lvl),
                                )}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuItem
                              onSelect={() => setCopyTarget(l.id)}
                            >
                              {t('copy_from_menu')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </th>
                    {users.map((u) => {
                      const level = effective(u.id, l.id);
                      return (
                        <Cell
                          key={u.id}
                          userId={u.id}
                          locationId={l.id}
                          level={level}
                          label={levelLabel(level)}
                          ariaLabel={t('cell_label', {
                            user: u.name,
                            location: l.name,
                            level: levelLabel(level),
                          })}
                          locked={!!u.locked}
                          onClick={onCellClick}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Popover
        open={openCell !== null}
        onOpenChange={(open) => {
          if (!open) setOpenCell(null);
        }}
      >
        <PopoverAnchor virtualRef={anchorRef} />
        <PopoverContent
          className="w-64 p-3"
          align="start"
          onCloseAutoFocus={(e) => {
            // Hand focus back to the cell, not the (absent) trigger.
            e.preventDefault();
            anchorRef.current?.focus();
          }}
        >
          {openCell && (
            <CellEditor
              title={`${userName(openCell.userId)} · ${locationName(
                openCell.locationId,
              )}`}
              level={effective(openCell.userId, openCell.locationId)}
              onChange={(level) =>
                applyLevels([[openCell.userId, openCell.locationId]], level)
              }
              label={levelLabel}
            />
          )}
        </PopoverContent>
      </Popover>

      <AddUserDialog
        portfolioId={portfolioId}
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={refresh}
      />

      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tAdmin('are_you_sure')}</DialogTitle>
            <DialogDescription>
              {t('remove_user_description', { name: userName(removeTarget) })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={removing}
              onClick={confirmRemove}
            >
              {removing ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <TrashIcon className="h-4 w-4" />
              )}
              {t('delete_account')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={copyTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCopyTarget(null);
            setCopySource('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('copy_title', { location: locationName(copyTarget) })}
            </DialogTitle>
            <DialogDescription>{t('copy_description')}</DialogDescription>
          </DialogHeader>
          <GroupSelector
            groups={data.locations
              .filter((l) => l.id !== copyTarget)
              .map((l) => ({ value: `${l.id}`, label: l.name }))}
            placeholder={t('pick_location')}
            emptyString={t('no_location')}
            value={copySource}
            onChange={setCopySource}
          />
          {copySource && (
            <p className="text-muted-foreground text-sm">
              {copyChanges.length === 0
                ? t('copy_identical')
                : t('copy_count', { count: copyChanges.length })}
              {lockedUsers.size > 0 && ` ${t('copy_locked')}`}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyTarget(null)}>
              {t('cancel')}
            </Button>
            <Button disabled={copyChanges.length === 0} onClick={copyLocation}>
              {t('copy')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// The per-cell editor: "Manage all" as select-all, then the three permissions.
// Ticking a manage permission ticks View too; unticking View clears everything.
function CellEditor({
  title,
  level,
  onChange,
  label,
}: {
  title: string;
  level: AccessLevel;
  onChange: (level: AccessLevel) => void;
  label: (level: AccessLevel) => string;
}) {
  const flags = flagsOf(level);
  const all = level === 'all';
  const some = level !== 'none' && !all;

  const toggle = (flag: keyof LevelFlags, on: boolean) => {
    if (flag === 'view') {
      onChange(on ? levelOf({ ...flags, view: true }) : 'none');
      return;
    }
    onChange(levelOf({ ...flags, [flag]: on, view: flags.view || on }));
  };

  const row = (
    id: string,
    checked: boolean | 'indeterminate',
    text: string,
    onCheckedChange: (on: boolean) => void,
  ) => (
    <label
      htmlFor={id}
      className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(c) => onCheckedChange(c === true)}
      />
      {text}
    </label>
  );

  return (
    <div className="flex flex-col gap-1">
      <p className="text-muted-foreground truncate px-2 pb-1 text-xs">
        {title}
      </p>
      {row(
        'cell-all',
        all ? true : some ? 'indeterminate' : false,
        label('all'),
        (on) => onChange(on ? 'all' : 'none'),
      )}
      <Separator className="my-1" />
      {row('cell-view', flags.view, label('view'), (on) => toggle('view', on))}
      {row('cell-groups', flags.groups, label('groups'), (on) =>
        toggle('groups', on),
      )}
      {row('cell-users', flags.users, label('users'), (on) =>
        toggle('users', on),
      )}
    </div>
  );
}
